import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import jwt_decode from 'jwt-decode';
import median from 'median-js-bridge';
import React, { SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loadingAccessToken } from '../../../app/Slices/AuthSlice';
import {
    useSendVerificationEmailMutation,
    useSignInMutation,
    useSignOutMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import {
    ActionType,
    selectSnackBars,
    Toast,
} from '../../../app/Slices/SnackBarSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomCheckbox } from '../../../layout/styledcomponents/CustomCheckbox';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import {
    ANDROID_PLAY_STORE_URL,
    IOS_APP_STORE_URL,
} from '../../../shared/appDeepLink';
import { homePathForRole } from '../../../shared/homePathForRole';
import ForgotPasswordModal from '../../components/Login/ForgotPasswordModal';

import toast from '../../shared/Toast';
import { clearProfileSetupBannerDismissed } from '../../components/Profile/profileSetupBanner';

const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export default function LoginPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [signIn] = useSignInMutation();
    const [signOut] = useSignOutMutation();
    const [openModal, setOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [password, setPassword] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [isValidInput, setIsValidInput] = useState(false);
    const [sendVerificationEmail] = useSendVerificationEmailMutation();
    const [staySignedIn, setStaySignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const toasts = useSelector(selectSnackBars);
    const theme = useTheme();
    const location = useLocation();
    const previousLocation = location.state?.from?.pathname
        ? location.state?.from?.pathname + (location.state?.from?.search || '')
        : undefined;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        signOut({});
    }, [signOut]);

    const paperStyle = {
        padding: '0px 0px 0px 0px',
        minWidth: 380,
        maxWidth: 480,
        borderRadius: 20,
        boxShadow:
            '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    };
    const btnstyle = {
        margin: '8px 0px  30px  0px',
        height: '50px',
        fontSize: '16px',
        borderRadius: `${theme.palette.custom.button.borderRadius}`,
    };

    const errors: any = [];

    if (emailAddress.length === 0) {
        errors.push('Email cannot be empty');
    }

    if (password.length === 0) {
        errors.push('Password cannot be empty');
    }

    useEffect(() => {
        if (toasts.find((toast: Toast) => toast.id === 'faceRecWarning')) {
            toast.remove('faceRecWarning');
        }
        toast.remove('finishSettingUpProfile');
        toast.remove('finishSettingUpBilling');
        localStorage.removeItem('visitTypePref');
        localStorage.removeItem('username');
        localStorage.removeItem('password');
        localStorage.removeItem('rememberMe');
        clearProfileSetupBannerDismissed();
    }, []);

    const handleSendVerificationEmail = (email: string) => {
        sendVerificationEmail({ email })
            .then((data: any) => {
                if (data?.error) {
                    toast.error(data.error.data.message);
                } else {
                    toast.remove('emailNotVerified');
                    toast.success('Verification email is sent successfully.');
                }
            })
            .catch(() => {
                toast.error(
                    'Failed to send verification email. Please try again.',
                );
            });
    };

    const handleSignInEmail = (event: {
        target: { value: SetStateAction<string> };
    }) => {
        setEmailAddress(event.target.value);
        if (validatEmail(event.target.value) === false) {
            setIsValidEmail(true);
        } else {
            setIsValidEmail(false);
        }
    };

    const onClickOpenModal = () => {
        setOpen(true);
    };
    const handleChangeModal = () => {
        setOpen(false);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleSignUp = () => {
        navigate(`/register`);
    };

    const validatEmail = (email: any) => {
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
    };

    const handlePassword = (event: {
        target: { value: SetStateAction<string> };
    }) => {
        setPassword(event.target.value);
        if (validatePassword(event.target.value) === false) {
            setIsValidInput(true);
        } else {
            setIsValidInput(false);
        }
    };

    const validatePassword = (pw: any) => {
        return (
            /[A-Z]/.test(pw) &&
            pw.length >= 8 &&
            /[a-z]/.test(pw) &&
            /[0-9]/.test(pw) &&
            /[#?!@$%^&*-]/.test(pw)
        );
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const signInFromForm = (form: HTMLFormElement) => {
        const formData = new FormData(form);
        const email = String(formData.get('email') ?? '');
        const submittedPassword = String(formData.get('password') ?? '');
        if (validatEmail(email) === false) {
            toast.error(`Invalid Email`);
            return;
        }
        if (validatePassword(submittedPassword) === false) {
            toast.error(`Invalid Password`);
            return;
        }
        const requestBody = {
            email,
            password: submittedPassword,
            staySignedIn,
        };
        signIn(requestBody).then((data: any) => {
            if (data) {
                if (data?.error) {
                    if (data.error.data.error === 'auth.unverified_email') {
                        toast.error(`Email address is not verified.`, {
                            actionText: 'Resend verification email',
                            actionOnclick: () =>
                                handleSendVerificationEmail(email),
                            actionType: ActionType.DEFAULT,
                            customId: 'emailNotVerified',
                        });
                    } else {
                        toast.error(`${data?.error?.data.message}`);
                    }
                } else {
                    dispatch(loadingAccessToken(data?.data));
                    const userToken: any = data?.data.accessToken;
                    const decode: any = jwt_decode(userToken);

                    median.onesignal.externalUserId.set({
                        externalId: decode?.sub.toString(),
                    });
                    if (
                        previousLocation === undefined ||
                        previousLocation === '/' ||
                        previousLocation === '/login' ||
                        previousLocation === '' ||
                        previousLocation.includes('reset') ||
                        previousLocation.includes('verifyEmail') ||
                        previousLocation.includes('register')
                    ) {
                        navigate(homePathForRole(decode?.role), {
                            replace: true,
                        });
                    } else {
                        navigate(previousLocation, { replace: true });
                    }
                }
            } else {
                toast.error(`User does not exist`);
            }
        });
    };

    const onSubmitSignIn = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInFromForm(event.currentTarget);
    };

    const onFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key !== 'Enter') return;
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name !== 'email' && target.name !== 'password') return;
        event.preventDefault();
        signInFromForm(event.currentTarget);
    };

    return (
        <Box
            component='main'
            sx={{
                position: 'relative',
                width: '100vw',
                minHeight: 'var(--app-viewport-height)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: theme.palette.custom.loggedOutBackgroundImage,
                backgroundAttachment: { xs: 'scroll', md: 'fixed' },
                backgroundSize: 'cover',
                overflow: 'auto',
                p: { xs: 2, sm: 3 },
            }}
        >
            <Grid component='section' aria-label='Login'>
                <Paper elevation={isMobile ? 0 : 10} style={paperStyle}>
                    <Grid
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme.palette.neutral.main,
                            justifyContent: 'center',
                            alignItems: 'center',
                            alignContent: 'center',
                            padding: '35px',
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                        }}
                    >
                        <img
                            alt='primarylogo'
                            src={theme.palette.custom.lightLogo}
                            style={{
                                width: '170px',
                                height: 'auto',
                            }}
                        />{' '}
                    </Grid>

                    <Grid sx={{ padding: '0px 40px 40px 40px' }}>
                        <Typography
                            component='h1'
                            variant='h5'
                            color='custom.bodyTextColor'
                            sx={{
                                fontSize: '28px',
                                letterSpacing: '-0.5px',
                                padding: '20px 0px 20px 5px',
                            }}
                        >
                            Sign In
                        </Typography>{' '}
                        <Box
                            component='form'
                            onSubmit={onSubmitSignIn}
                            onKeyDown={onFormKeyDown}
                            noValidate
                        >
                            <TextField
                                error={isValidEmail}
                                label={
                                    isValidEmail === true
                                        ? 'Invalid Email Address'
                                        : ''
                                }
                                name='email'
                                type='email'
                                placeholder='Email'
                                autoComplete='username'
                                onChange={handleSignInEmail}
                                value={emailAddress}
                                fullWidth
                                sx={{ marginBottom: '10px' }}
                                inputProps={{
                                    'aria-label': 'Email',
                                }}
                                InputProps={{
                                    style: {
                                        height: '50px',
                                    },
                                    startAdornment: (
                                        <InputAdornment
                                            position='start'
                                            sx={{ tabIndex: '-1' }}
                                        >
                                            <EmailIcon
                                                fontSize='small'
                                                sx={{
                                                    color: theme.palette.faded
                                                        .main,
                                                    tabIndex: '-1',
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                error={isValidInput}
                                label={
                                    isValidInput === true
                                        ? 'Invalid Password'
                                        : ''
                                }
                                name='password'
                                placeholder='Password'
                                autoComplete='current-password'
                                type={showPassword ? 'text' : 'password'}
                                onChange={handlePassword}
                                value={password}
                                fullWidth
                                inputProps={{
                                    'aria-label': 'Password',
                                }}
                                InputProps={{
                                    style: {
                                        height: '50px',
                                    },
                                    startAdornment: (
                                        <InputAdornment
                                            position='start'
                                            sx={{ tabIndex: '-1' }}
                                        >
                                            <LockIcon
                                                fontSize='small'
                                                sx={{
                                                    color: theme.palette.faded
                                                        .main,
                                                    tabIndex: '-1',
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment
                                            position='end'
                                            sx={{ tabIndex: '-1' }}
                                        >
                                            {showPassword === false ? (
                                                <CustomIconButton
                                                    type='button'
                                                    tabIndex='-1'
                                                    onClick={() =>
                                                        setShowPassword(
                                                            !showPassword,
                                                        )
                                                    }
                                                    sx={{
                                                        width: '100%',
                                                        height: '25px',
                                                        display: 'flex',
                                                        flexDirection: 'row',

                                                        '&:hover': {
                                                            cursor: 'pointer',
                                                            color: theme.palette
                                                                .textAccent
                                                                .main,
                                                            backgroundColor:
                                                                'transparent',
                                                            background: 'none',
                                                        },
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize='small' />
                                                </CustomIconButton>
                                            ) : (
                                                <CustomIconButton
                                                    type='button'
                                                    onClick={() =>
                                                        setShowPassword(
                                                            !showPassword,
                                                        )
                                                    }
                                                    sx={{
                                                        width: '100%',
                                                        height: '25px',
                                                        display: 'flex',
                                                        flexDirection: 'row',

                                                        '&:hover': {
                                                            cursor: 'pointer',
                                                            color: theme.palette
                                                                .textAccent
                                                                .main,
                                                            backgroundColor:
                                                                'transparent',
                                                            background: 'none',
                                                        },
                                                    }}
                                                >
                                                    <VisibilityOffIcon fontSize='small' />
                                                </CustomIconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <CustomCheckbox
                                        checked={staySignedIn}
                                        onChange={(
                                            event: React.ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            setStaySignedIn(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                }
                                label='Keep me signed in'
                                sx={{
                                    ml: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '14px',
                                        color: 'text.primary',
                                    },
                                }}
                            />
                            <Grid sx={{ marginTop: '30px' }}>
                                <CustomButton
                                    type='submit'
                                    variant='contained'
                                    color='secondary'
                                    style={btnstyle}
                                    fullWidth
                                    disabled={
                                        isValidEmail ||
                                        isValidInput ||
                                        errors.length > 0
                                    }
                                >
                                    Sign in
                                </CustomButton>
                            </Grid>
                        </Box>
                        <Grid sx={{ display: 'flex', width: '100%' }}>
                            <Grid
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    justifyContent: 'center',
                                    gap: 2,
                                }}
                            >
                                <Link target='_blank' to={IOS_APP_STORE_URL}>
                                    <img
                                        alt='Download on the App Store'
                                        src='/AppleWhite.png'
                                        style={{ height: '40px' }}
                                    />
                                </Link>

                                {!isIOS && (
                                    <Link
                                        target='_blank'
                                        to={ANDROID_PLAY_STORE_URL}
                                    >
                                        <img
                                            alt='Get it on Google Play'
                                            src='/googleWhite.png'
                                            style={{ height: '40px' }}
                                        />
                                    </Link>
                                )}
                            </Grid>
                        </Grid>
                        <Box textAlign='center'>
                            <Button
                                onClick={onClickOpenModal}
                                color='primary'
                                sx={{
                                    textTransform: 'none',
                                    fontFamily: theme.typography.fontFamily,
                                    fontSize: '.75rem',
                                }}
                            >
                                Forgot password?
                            </Button>
                        </Box>
                    </Grid>
                </Paper>
            </Grid>
            <ForgotPasswordModal
                open={openModal}
                onClose={handleCloseModal}
                onChange={handleChangeModal}
            />
        </Box>
    );
}
